import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { portalApi, type PortalTermsStatusDto } from '@/api/portal'
import { Button } from '@/components/ui/button'
import { FileText, ExternalLink } from 'lucide-react'

export function TermsGateModal({ status, companyName }: { status: PortalTermsStatusDto; companyName?: string | null }) {
  const queryClient = useQueryClient()
  const [checked, setChecked] = useState(false)

  const viewTerms = async () => {
    // Open the tab synchronously, inside the click's user-gesture context — mobile Safari
    // (and some mobile Chrome) silently blocks window.open() called after an await.
    const win = window.open('', '_blank')
    const res = await portalApi.getTermsDocument()
    const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/pdf' }))
    if (win) win.location.href = url
    else window.open(url, '_blank')
  }

  const acceptMutation = useMutation({
    mutationFn: () => portalApi.acceptTerms(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-terms-status'] }),
  })

  const isUpdate = status.acceptedVersion != null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-2 text-gray-900">
          <FileText size={20} className="text-indigo-500" />
          <h2 className="text-lg font-semibold">
            {isUpdate ? 'Terms & Conditions updated' : 'Terms & Conditions'}
          </h2>
        </div>
        <p className="text-sm text-gray-600">
          {isUpdate
            ? `${companyName ?? 'This training company'} has updated their Terms & Conditions. Please review and accept the new version to continue.`
            : `Before continuing, please review and accept ${companyName ?? 'this training company'}'s Terms & Conditions.`}
        </p>
        <button
          type="button"
          onClick={viewTerms}
          className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
        >
          <ExternalLink size={14} /> View Terms & Conditions (v{status.termsVersion})
        </button>
        <label className="flex items-start gap-2.5 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-700">
            I have read and accept the Terms &amp; Conditions
          </span>
        </label>
        {acceptMutation.isError && (
          <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
        )}
        <Button
          className="w-full"
          disabled={!checked || acceptMutation.isPending}
          onClick={() => acceptMutation.mutate()}
        >
          {acceptMutation.isPending ? 'Saving…' : 'Accept & continue'}
        </Button>
      </div>
    </div>
  )
}
